import type { Attachment, ChannelMediaKind } from '@novachat/shared';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { ImageListEnds } from '../components/Attachments';
import { client } from './client';

/** One kind of what was shared in a channel, newest first, a page at a time (GET /channels/:id/media). */
export function useChannelMedia(channelId: string, kind: ChannelMediaKind) {
  return useInfiniteQuery({
    queryKey: ['channel-media', channelId, kind],
    queryFn: ({ pageParam }) =>
      client.api.channelMedia(channelId, {
        kind,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '',
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
}

/** Pages read at most to find the opened image, or to reach an older image past pages of videos. */
const PAGES_PER_LOOKUP = 5;

const isImage = (attachment: Attachment | undefined): attachment is Attachment => !!attachment?.mime.startsWith('image/');

/**
 * All images of a channel for the viewer, oldest first, opened at `start`: an image of message `messageId`,
 * one of `own` (that message's images). The channel's media come newest first, so everything newer than a
 * loaded image is loaded too, and older pages load as the viewer reaches the oldest one. Until `start` turns
 * up (a refetch when it is newer than the cache, else up to five older pages) the viewer shows `own`; when it
 * never does (thread replies are not in the list), it stays with them.
 */
export function useChannelImages(
  channelId: string,
  messageId: string,
  start: Attachment,
  own: readonly Attachment[],
): { images: readonly Attachment[]; ends?: ImageListEnds } {
  const queries = useQueryClient();
  const media = useChannelMedia(channelId, 'media');
  const items = media.data?.pages.flatMap((page) => page.items) ?? [];
  const images = items.flatMap((item) => (isImage(item.attachment) ? [item.attachment] : [])).reverse();
  const found = images.some((image) => image.id === start.id);
  const [settled, setSettled] = useState<'channel' | 'own' | null>(null);
  const mode = settled ?? (found ? 'channel' : null);
  const [openedAt] = useState(Date.now);
  const lookup = useRef({ refreshed: false, pages: 0 });

  useEffect(() => {
    if (settled) return;
    if (found) return setSettled('channel');
    // The live state, not this render's: an effect run twice (StrictMode) must not ask twice or give up.
    if (queries.isFetching({ queryKey: ['channel-media', channelId, 'media'] }) > 0) return;
    const newest = items[0]?.messageId;
    const oldest = items.at(-1)?.messageId;
    // Message ids are UUIDv7: in text order they are in time order.
    if (media.data && !lookup.current.refreshed && media.dataUpdatedAt < openedAt && (newest === undefined || messageId > newest)) {
      // Sent after these pages were read: read them again.
      lookup.current.refreshed = true;
      void media.refetch({ cancelRefetch: false });
    } else if (oldest !== undefined && messageId <= oldest && media.hasNextPage && !media.isFetchNextPageError && lookup.current.pages < PAGES_PER_LOOKUP) {
      lookup.current.pages += 1;
      void media.fetchNextPage({ cancelRefetch: false });
    } else setSettled('own');
  }, [settled, found, media.isFetching, media.data, media.dataUpdatedAt, media.hasNextPage, media.isFetchNextPageError]);

  const [loadingOlder, setLoadingOlder] = useState(false);
  const busy = useRef(false);
  const loadOlder = async () => {
    if (busy.current) return;
    busy.current = true;
    setLoadingOlder(true);
    try {
      // A page may hold only videos: read on until an older image turns up.
      for (let page = 0; page < PAGES_PER_LOOKUP; page += 1) {
        const result = await media.fetchNextPage({ cancelRefetch: false });
        if (result.isFetchNextPageError || !result.hasNextPage || result.data?.pages.at(-1)?.items.some((item) => isImage(item.attachment))) break;
      }
    } finally {
      busy.current = false;
      setLoadingOlder(false);
    }
  };

  if (mode === 'channel' && images.length > 0) {
    return {
      images,
      ends: {
        before: media.hasNextPage,
        after: false,
        loading: loadingOlder ? 'before' : null,
        load: (side) => {
          if (side === 'before') void loadOlder();
        },
      },
    };
  }
  // Still looking: the message's own images, with both ends not known yet.
  if (mode === null) return { images: own, ends: { before: null, after: null, loading: null, load: () => {} } };
  return { images: own };
}
